/**
 * AI Function Node Component
 *
 * Dedicated visual for the AI Function node (deterministic, single-function call — no LLM).
 * Every AI Function palette template shares the node code "ai-function" but is spread across
 * functional categories (e.g. "ai", "data"), so the shape is selected by CODE — not category —
 * and rendered with this component (registered under the React Flow type "ai-function-node").
 *
 * It reuses the standard CustomNode rendering (template color/icon, description, ports,
 * execution-status badge) and adds an "ƒ AI Function" badge plus an accent so the node reads
 * as a function call at a glance, while still honouring each function's own colour/icon.
 */

import React from 'react';
import { NodeProps } from 'reactflow';
import { WorkflowNode } from '@flow-studio/types';
import { CustomNodeClass } from './CustomNode';
import { withNodeHooks, NodeConfig } from './Base/BaseNode';

class AIFunctionNodeClass extends CustomNodeClass {
  protected config(): NodeConfig {
    return {
      ...super.config(),
      customClassName: 'ai-function-node',
    };
  }

  protected getClassName(): string {
    const { selected, data } = this.props;
    return `custom-node ai-function-node ${selected ? 'selected' : ''} ${data.processElement?.isDisabled ? 'disabled' : ''}`;
  }

  protected renderContent(): React.ReactNode {
    return (
      <>
        {super.renderContent()}
        <span
          title="AI Function — deterministic single-function call (no LLM)"
          style={{
            position: 'absolute',
            top: '-9px',
            right: '8px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            padding: '1px 6px',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
            color: 'white',
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            boxShadow: '0 1px 4px rgba(67, 56, 202, 0.5)',
            zIndex: 5,
            pointerEvents: 'none',
          }}
        >
          <span style={{ fontStyle: 'italic', fontFamily: 'Georgia, serif' }}>ƒ</span> AI FUNCTION
        </span>
      </>
    );
  }
}

export const AIFunctionNode: React.FC<NodeProps<WorkflowNode['data']>> =
  withNodeHooks(AIFunctionNodeClass);
